import { DatePickerSingle } from 'src/components/date-picker/date-picker-single';
import { matchOnlyNumber } from 'src/utils/validators';
import { useLocalization } from 'src/utils/useLocalization';
import React, { useState } from 'react';
import './EditMode.scss';

const FIRST_INDEX = 0;
const MAX_STOCK_NUMBER_LENGTH = 8;

interface EditModeProps {
    onLoadValue?: any;
    inputFieldType?: any;
    isNumeric?: boolean;
    isAlphanumeric?: boolean;
    replacingParentId?: any;
    replacingElementId?: string;
    updateEditFieldValue: (arg?: any) => void;
}

const EditMode = ({
    onLoadValue,
    inputFieldType,
    isNumeric,
    isAlphanumeric,
    replacingParentId,
    replacingElementId,
    updateEditFieldValue,
}: EditModeProps) => {
    const {
        errorMessages: { NOT_NUMBER },
        labelsCommon: { EMPTY_FIELD },
    } = useLocalization();

    const onLoadFieldValue = onLoadValue === EMPTY_FIELD ? '' : onLoadValue;
    const [fieldValue, setFieldValue] = useState<any>(onLoadFieldValue);
    const [saveBtnDisabled, setSaveBtnDisabled] = useState<boolean>(true);
    const [errorMsg, setErrorMsg] = useState<string>('');

    function handleTextareaUpdate(value?: any) {
        value = value === EMPTY_FIELD ? '' : value;
        setFieldValue(value);
        setSaveBtnDisabled(false);
    }

    function validateInput(value?: any) {
        if (value === '') {
            setErrorMsg('');
            setSaveBtnDisabled(false);
            return true;
        } else if (!isNumeric || matchOnlyNumber(value)) {
            setErrorMsg('');
            setSaveBtnDisabled(false);
            return true;
        } else {
            setErrorMsg(NOT_NUMBER);
            setSaveBtnDisabled(true);
            return false;
        }
    }

    function handleDate(startDate: string) {
        setFieldValue(startDate === '' ? null : startDate);
        setSaveBtnDisabled(false);
    }

    const inputField = (inputFieldType?: any) => {
        switch (inputFieldType) {
            case 'text':
                return (
                    <input
                        className={`edit-input-field ${
                            errorMsg !== '' ? 'edit-input-error' : ''
                        }`}
                        id='edit-input-field'
                        name='name'
                        value={fieldValue ?? ''}
                        maxLength={
                            isAlphanumeric ? MAX_STOCK_NUMBER_LENGTH : undefined
                        }
                        onChange={(element) => {
                            const value = isAlphanumeric
                                ? element.target.value
                                      .replace(/[^a-z0-9]/gi, '')
                                      .slice(
                                          FIRST_INDEX,
                                          MAX_STOCK_NUMBER_LENGTH,
                                      )
                                : element.target.value;
                            setFieldValue(value);
                            validateInput(value);
                        }}
                        onBlur={(element) =>
                            validateInput(element.target.value)
                        }
                    />
                );

            case 'textArea':
                return (
                    <textarea
                        value={fieldValue ?? ''}
                        className='edit-text-area'
                        onChange={(element) => {
                            handleTextareaUpdate(element.target.value);
                        }}
                    />
                );

            case 'datePicker':
                return (
                    <div className='edit-date-wrapper'>
                        <DatePickerSingle
                            value={fieldValue}
                            handleOnClose={handleDate}
                            totalPastYearsDisplayValue={3}
                        />
                    </div>
                );

            default:
                return null;
        }
    };

    function handleSave() {
        updateEditFieldValue({
            action: 'save',
            value: fieldValue,
            replacingElementId: replacingElementId,
            replacingParentId: replacingParentId,
        });
    }

    function handleCancel() {
        setErrorMsg('');
        setFieldValue(onLoadFieldValue);
        updateEditFieldValue({
            action: 'cancel',
            replacingElementId: replacingElementId,
            replacingParentId,
        });
    }

    return (
        <div className='edit-mode-wrapper' id='edit-mode-component'>
            <div className='input-wrapper'>
                {inputField(inputFieldType)}
                {errorMsg && <div className='error-message'>{errorMsg}</div>}
            </div>
            <div
                className={`actionable-btns ${
                    errorMsg !== '' ? 'actionable-btns-error' : ''
                }`}
            >
                <button
                    className='save-btn'
                    onClick={handleSave}
                    disabled={saveBtnDisabled}
                >
                    <i
                        className='c-btn__icon c-icon c-icon--[semantic-checkmark]'
                        aria-hidden='true'
                        role='img'
                    ></i>
                </button>
                <button className='close-btn' onClick={handleCancel}>
                    <i
                        className='c-btn__icon c-icon c-icon--[semantic-close]'
                        aria-hidden='true'
                        role='img'
                    ></i>
                </button>
            </div>
        </div>
    );
};

export default EditMode;
